import React from "react";
import { Star, ExternalLink } from "lucide-react";
import { Button } from "../ui/button";

// Google rating summary — renders ONLY when the owner entered BOTH a rating
// and a review count. No fake testimonials, no "no reviews yet" states.
// Desktop presents it as centered social proof; mobile keeps a compact
// stacked layout.
export const PublicBusinessReviews = ({ business, t }) => {
  if (!business) return null;
  const rating = business.google_rating;
  const count = business.google_review_count;
  if (rating == null || count == null) return null;
  // Prefer the Google review link the owner already saved in Settings
  // (single source of truth); legacy stored Business Profile URLs remain a
  // safe fallback — no data deleted.
  const url = (business.google_review_link || business.google_business_url || "").trim();
  const fullStars = Math.round(Math.min(Math.max(rating, 0), 5));

  return (
    <section
      className="rounded-2xl border border-green-100 bg-gradient-to-br from-white via-emerald-50/40 to-white p-6 sm:p-10 lg:p-14 mb-5 lg:mb-8 lg:text-center"
      data-testid="public-reviews-section"
    >
      <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 tracking-tight">
        {t("public.reviews.title")}
      </h2>
      <div className="mt-4 lg:mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between lg:flex-col lg:items-center gap-4 lg:gap-5">
        <div className="lg:flex lg:flex-col lg:items-center">
          <div className="flex items-center gap-3 lg:justify-center">
            <span
              className="font-display text-4xl lg:text-6xl font-semibold text-gray-900"
              data-testid="public-reviews-rating"
            >
              {rating}
            </span>
            <span className="flex items-center gap-0.5" aria-hidden="true">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star
                  key={i}
                  className={`w-5 h-5 lg:w-6 lg:h-6 ${i <= fullStars ? "text-amber-500 fill-amber-500" : "text-gray-200 fill-gray-200"}`}
                />
              ))}
            </span>
          </div>
          <p className="mt-1.5 lg:mt-2.5 text-sm lg:text-base text-gray-500" data-testid="public-reviews-count">
            {t("public.reviews.basedOn", count)}
          </p>
        </div>
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer" className="shrink-0">
            <Button
              variant="outline"
              className="h-11 px-5 lg:px-6 font-semibold border-gray-300 bg-white text-gray-900 hover:bg-gray-50 w-full sm:w-auto"
              data-testid="public-reviews-view-profile"
            >
              {t("public.reviews.viewProfile")} <ExternalLink className="w-4 h-4 ml-1.5" />
            </Button>
          </a>
        )}
      </div>
    </section>
  );
};
